import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, RevealLines } from "@/components/Reveal";
import { HeroIntro } from "@/components/HeroIntro";
import { Marquee } from "@/components/Marquee";
import { ServicesInteractive } from "@/components/ServicesInteractive";
import { ProjectsEditorial } from "@/components/ProjectsEditorial";
import { GardenMaterials } from "@/components/GardenMaterials";
import { GardenFinder } from "@/components/GardenFinder";
import { RegionMap } from "@/components/RegionMap";
import { FaqSection } from "@/components/FaqSection";
import { QuoteForm } from "@/components/QuoteForm";
import { CtaBand } from "@/components/CtaBand";
import { ATMO, IMG } from "@/config/site";

const PRINCIPLES = [
  { n: "01", t: "Ort zuerst", d: "Licht, Boden, Sichtachsen und Wege bestimmen den Entwurf – nicht der Katalog." },
  { n: "02", t: "Wenige Materialien", d: "Stein, Holz, Grün und Licht in ruhiger Kombination statt vieler Effekte." },
  { n: "03", t: "Handwerk", d: "Sauberer Aufbau, präzise Kanten, verlässliche Details – auch dort, wo man nicht hinsieht." },
  { n: "04", t: "Zeit mitdenken", d: "Ein Garten wird erst über Jahre gut. Wir planen und pflegen entsprechend." },
];

const Philosophy = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#F3EDE2] py-28 md:py-44" data-testid="philosophy-section">
      <div className="mx-auto max-w-[1480px] px-5 md:px-10">
        <div className="grid items-center gap-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-28">
          <div>
            <p className="eyebrow">Unsere Philosophie</p>
            <RevealLines
              lines={["Ein Garten", "ist kein Produkt."]}
              as="h2"
              className="display-xl mt-6 text-[11vw] leading-[0.9] text-[#17251C] md:text-[5.2vw]"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-lg leading-relaxed text-[#2F3D2E] md:text-xl">Er ist ein Ort zum Leben.</p>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#6E7A69]">
                Wir arbeiten wie ein kleines Landschaftsatelier: konzentriert, materialbewusst und mit Respekt vor dem
                bestehenden Grundstück. Statt möglichst vieler Elemente entsteht ein ruhiger Außenraum, der über Jahre
                besser wird.
              </p>
              <Link to="/ueber-uns" className="btn-outline mt-10" data-testid="philosophy-about-link">
                Über Hanse Garten <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>

          <div className="relative">
            <div className="overflow-hidden blob-mask">
              <motion.img
                style={{ y }}
                src={ATMO.editorial}
                alt="Moderner Außenbereich mit Sitzgruppe, Naturstein und dichter Bepflanzung"
                className="aspect-[4/5] w-full scale-110 object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="absolute -bottom-6 left-0 max-w-[15rem] rounded-[1.6rem] bg-[#17251C] p-6 text-[#DCCDB4] md:-left-10"
            >
              <p className="display-xl text-3xl text-[#F3EDE2]">Bremen</p>
              <p className="mt-2 text-xs leading-relaxed">
                Gestaltung, Bau und Pflege aus einer Hand – regional verwurzelt.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo
        title="Hanse Garten Bremen | Räume, die wachsen – Gartengestaltung & Landschaftsbau"
        description="Hanse Garten Bremen gestaltet Gärten mit Charakter: Gartenplanung, Landschaftsbau, Terrassen, Pflasterarbeiten, Bepflanzung und Gartenpflege in Bremen & Umgebung."
        path="/"
        image={ATMO.hero}
      />

      <HeroIntro />

      {/* Manifest / Prinzipien */}
      <section className="relative overflow-hidden bg-[#2F3D2E] py-24 md:py-32" data-testid="principles-section">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="eyebrow !text-[#DCCDB4]">Haltung</p>
              <RevealLines
                lines={["Vier Grundsätze."]}
                as="h2"
                className="display-xl mt-5 text-[9vw] leading-[0.92] text-[#F3EDE2] md:text-[3.6vw]"
              />
            </div>
            <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.08}>
                  <span className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-[#B4623C]">{p.n}</span>
                  <h3 className="display-xl mt-4 text-2xl text-[#F3EDE2]">{p.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#B8C3B2]">{p.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Philosophy />

      {/* Leistungen – interaktive vertikale Navigation */}
      <section className="bg-[#DCCDB4]/30 py-28 md:py-40" data-testid="services-section">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="eyebrow">Leistungen</p>
              <RevealLines
                lines={["Was wir bauen."]}
                as="h2"
                className="display-xl mt-5 text-[11vw] leading-[0.9] text-[#17251C] md:text-[4.8vw]"
              />
            </div>
            <Reveal delay={0.15}>
              <Link to="/leistungen" className="btn-outline" data-testid="services-all-link">
                Alle Leistungen <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
          <ServicesInteractive />
        </div>
      </section>

      <GardenMaterials />

      {/* Projekte */}
      <section className="bg-[#F3EDE2] py-28 md:py-40" data-testid="projects-section">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="mb-16 max-w-3xl">
            <p className="eyebrow">Projekte</p>
            <RevealLines
              lines={["Gärten mit", "Charakter."]}
              as="h2"
              className="display-xl mt-5 text-[12vw] leading-[0.9] text-[#17251C] md:text-[5.2vw]"
            />
          </div>
          <ProjectsEditorial />
          <Reveal delay={0.1}>
            <Link to="/projekte" className="btn-outline mt-16" data-testid="projects-all-link">
              Alle Projekte <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* Gartenfinder */}
      <section className="bg-[#F3EDE2] py-24 md:py-36" data-testid="finder-section">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <GardenFinder />
        </div>
      </section>

      {/* Marke / Über */}
      <section className="relative overflow-hidden bg-[#DCCDB4]/30 py-28 md:py-40" data-testid="brand-section">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-16 lg:grid-cols-[1fr_1fr] lg:gap-24">
            <Reveal className="overflow-hidden arch-mask">
              <img
                src={IMG.terrasse}
                alt="Strukturierter Garten mit Rasenweg und Beeteinfassungen – Arbeit von Hanse Garten Bremen"
                className="aspect-[4/5] w-full object-cover"
              />
            </Reveal>
            <div className="flex flex-col justify-center">
              <p className="eyebrow">Über uns</p>
              <RevealLines
                lines={["Hanse Garten", "Bremen"]}
                as="h2"
                className="display-xl mt-5 text-[11vw] leading-[0.9] text-[#17251C] md:text-[4.6vw]"
              />
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-lg text-lg leading-relaxed text-[#2F3D2E]">
                  Wir verbinden natürliche Materialien, durchdachte Gestaltung und handwerkliche Umsetzung.
                </p>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-[#6E7A69]">
                  Hinter Hanse Garten Bremen steht Inhaber Salih Koluman. Sie sprechen direkt mit der Person, die Ihr
                  Projekt betreut – von der ersten Idee bis zur Übergabe.
                </p>
                <Link to="/ueber-uns" className="btn-primary mt-10" data-testid="brand-about-link">
                  Mehr über uns <ArrowUpRight size={15} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Region */}
      <section className="bg-[#F3EDE2] py-24 md:py-36" data-testid="region-section">
        <div className="mx-auto grid max-w-[1480px] gap-14 px-5 md:px-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <p className="eyebrow">Einsatzgebiet</p>
            <RevealLines
              lines={["Bremen &", "Umgebung."]}
              as="h2"
              className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#17251C] md:text-[3.8vw]"
            />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-md text-base leading-relaxed text-[#6E7A69]">
                Wir arbeiten in Bremen, Bremen-Nord und im direkten Umland. Kurze Wege bedeuten verlässliche Termine und
                persönliche Betreuung – auch nach der Umsetzung.
              </p>
              <ul className="mt-8 flex flex-wrap gap-2">
                {["Bremen", "Bremen-Nord", "Umgebung"].map((r) => (
                  <li
                    key={r}
                    className="rounded-full border border-[#17251C]/20 px-5 py-2 text-[0.62rem] font-bold uppercase tracking-[0.2em] text-[#2F3D2E]"
                  >
                    {r}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <RegionMap />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#DCCDB4]/30 py-24 md:py-36" data-testid="faq-section">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-5 md:px-10 lg:grid-cols-[0.75fr_1.25fr]">
          <div>
            <p className="eyebrow">FAQ</p>
            <RevealLines
              lines={["Häufige", "Fragen."]}
              as="h2"
              className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#17251C] md:text-[3.4vw]"
            />
          </div>
          <FaqSection />
        </div>
      </section>

      {/* Anfrage */}
      <section id="anfrage" className="bg-[#F3EDE2] py-24 md:py-36" data-testid="form-section">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <div className="mb-12 max-w-2xl">
            <p className="eyebrow">Anfrage</p>
            <RevealLines
              lines={["Projekt anfragen."]}
              as="h2"
              className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#17251C] md:text-[3.6vw]"
            />
          </div>
          <QuoteForm />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
